import { useEffect, useMemo, useState } from 'react';
import { AppDialogFrame } from '../../components/overlays/AppDialogFrame';
import { SupportErrorMessage } from '../../components/overlays/EngineeringSupportBanner';
import { useStaffSettingsState } from '../school-settings-v2/hooks/useStaffSettingsState';
import {
  useCreateRecurringFeeDefinitionMutation,
  useDeleteRecurringFeeDefinitionMutation,
  useListRecurringFeeDefinitionsQuery,
  useUpdateRecurringFeeDefinitionMutation,
} from '../../state/domainRouterApi';
import { Icon } from '../school-settings-v2/components/ui';

type RecurringFeeBillingCategory =
  | 'activities'
  | 'transportation'
  | 'food_service'
  | 'other';

type RecurringFeeDefinitionItem = {
  id: string;
  schoolId: string | null;
  name: string;
  monthlyAmount: number;
  billingCategory: RecurringFeeBillingCategory;
  billingDay: number;
  startMonth: number;
  endMonth: number;
  eligibleGrades: string[];
  prorateFirstMonth: boolean;
  isActive: boolean;
};

type RecurringFeeFormState = {
  name: string;
  monthlyAmount: string;
  billingCategory: RecurringFeeBillingCategory;
  billingDay: string;
  startMonth: string;
  endMonth: string;
  eligibleGrades: string[];
  prorateFirstMonth: boolean;
  isActive: boolean;
  error: string | null;
};

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => ({
  value: String(index + 1),
  label: new Date(Date.UTC(2026, index, 1)).toLocaleDateString('en-US', {
    month: 'long',
    timeZone: 'UTC',
  }),
}));

const CATEGORY_OPTIONS: Array<{ value: RecurringFeeBillingCategory; label: string }> = [
  { value: 'activities', label: 'Activities' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'food_service', label: 'Food service' },
  { value: 'other', label: 'Other' },
];

const buildDefaultForm = (): RecurringFeeFormState => ({
  name: '',
  monthlyAmount: '',
  billingCategory: 'activities',
  billingDay: '1',
  startMonth: '9',
  endMonth: '6',
  eligibleGrades: [],
  prorateFirstMonth: false,
  isActive: true,
  error: null,
});

const formatCurrency = (amount: number): string =>
  `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const categoryBadgeClassName = (category: RecurringFeeBillingCategory): string => {
  switch (category) {
    case 'activities':
      return 'border-[rgba(38,151,162,0.3)] bg-[rgba(38,151,162,0.08)] text-[#1d7e87]';
    case 'transportation':
      return 'border-[rgba(58,123,213,0.3)] bg-[rgba(58,123,213,0.08)] text-[#315f9f]';
    case 'food_service':
      return 'border-[rgba(217,149,53,0.3)] bg-[rgba(217,149,53,0.08)] text-[#9e6b1f]';
    default:
      return 'border-border bg-muted text-text-secondary';
  }
};

const normalizeDefinitions = (items: unknown[] | undefined): RecurringFeeDefinitionItem[] =>
  (items ?? []).map((item) => {
    const record = item as Record<string, unknown>;
    return {
      id: String(record.id ?? ''),
      schoolId: record.schoolId == null ? null : String(record.schoolId),
      name: String(record.name ?? ''),
      monthlyAmount: Number(record.monthlyAmount ?? 0),
      billingCategory: (
        record.billingCategory === 'transportation'
        || record.billingCategory === 'food_service'
        || record.billingCategory === 'other'
          ? record.billingCategory
          : 'activities'
      ) as RecurringFeeBillingCategory,
      billingDay: Number(record.billingDay ?? 1),
      startMonth: Number(record.startMonth ?? 1),
      endMonth: Number(record.endMonth ?? 1),
      eligibleGrades: Array.isArray(record.eligibleGrades)
        ? record.eligibleGrades.map((value) => String(value)).filter(Boolean)
        : [],
      prorateFirstMonth: Boolean(record.prorateFirstMonth),
      isActive: Boolean(record.isActive),
    };
  }).filter((item) => item.id && item.name);

const buildScheduleLabel = (definition: Pick<RecurringFeeDefinitionItem, 'billingDay' | 'startMonth' | 'endMonth'>): string => {
  const startLabel = MONTH_OPTIONS.find((option) => Number(option.value) === definition.startMonth)?.label ?? `Month ${definition.startMonth}`;
  const endLabel = MONTH_OPTIONS.find((option) => Number(option.value) === definition.endMonth)?.label ?? `Month ${definition.endMonth}`;
  return `Day ${definition.billingDay} of month, ${startLabel} through ${endLabel}`;
};

const buildFormFromDefinition = (definition: RecurringFeeDefinitionItem): RecurringFeeFormState => ({
  name: definition.name,
  monthlyAmount: String(definition.monthlyAmount),
  billingCategory: definition.billingCategory,
  billingDay: String(definition.billingDay),
  startMonth: String(definition.startMonth),
  endMonth: String(definition.endMonth),
  eligibleGrades: definition.eligibleGrades,
  prorateFirstMonth: definition.prorateFirstMonth,
  isActive: definition.isActive,
  error: null,
});

const isFormValid = (form: RecurringFeeFormState): boolean => {
  const monthlyAmount = Number(form.monthlyAmount);
  const billingDay = Number(form.billingDay);
  const startMonth = Number(form.startMonth);
  const endMonth = Number(form.endMonth);
  return Boolean(form.name.trim())
    && Number.isFinite(monthlyAmount)
    && monthlyAmount > 0
    && Number.isInteger(billingDay)
    && billingDay >= 1
    && billingDay <= 28
    && Number.isInteger(startMonth)
    && startMonth >= 1
    && startMonth <= 12
    && Number.isInteger(endMonth)
    && endMonth >= 1
    && endMonth <= 12;
};

export function RecurringFeeManager() {
  const { data, isLoading, isFetching } = useListRecurringFeeDefinitionsQuery();
  const [createDefinition, createDefinitionState] = useCreateRecurringFeeDefinitionMutation();
  const [updateDefinition, updateDefinitionState] = useUpdateRecurringFeeDefinitionMutation();
  const [deleteDefinition, deleteDefinitionState] = useDeleteRecurringFeeDefinitionMutation();
  const { settingsState } = useStaffSettingsState();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDefinition, setEditingDefinition] = useState<RecurringFeeDefinitionItem | null>(null);
  const [form, setForm] = useState<RecurringFeeFormState>(buildDefaultForm());
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);

  const definitions = useMemo(
    () => normalizeDefinitions(data?.items),
    [data?.items],
  );
  const gradeOptions = useMemo(
    () => {
      const fromGrades = settingsState.grades
        .map((grade) => String(grade.label ?? '').trim())
        .filter(Boolean);
      const fromTuitionRates = settingsState.tuitionRates
        .map((rate) => String(rate.gradeLabel ?? '').trim())
        .filter(Boolean);
      return Array.from(new Set([...fromGrades, ...fromTuitionRates])).sort((left, right) => left.localeCompare(right));
    },
    [settingsState.grades, settingsState.tuitionRates],
  );

  const busy = createDefinitionState.isLoading
    || updateDefinitionState.isLoading
    || deleteDefinitionState.isLoading;

  useEffect(() => {
    if (!isModalOpen) {
      setForm(buildDefaultForm());
      setEditingDefinition(null);
    }
  }, [isModalOpen]);

  const openCreateModal = () => {
    setEditingDefinition(null);
    setForm(buildDefaultForm());
    setIsModalOpen(true);
  };

  const openEditModal = (definition: RecurringFeeDefinitionItem) => {
    setEditingDefinition(definition);
    setForm(buildFormFromDefinition(definition));
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    setForm((current) => ({ ...current, error: null }));
    setFeedback(null);

    try {
      const payload = {
        name: form.name.trim(),
        monthlyAmount: Number(form.monthlyAmount),
        billingCategory: form.billingCategory,
        billingDay: Number(form.billingDay),
        startMonth: Number(form.startMonth),
        endMonth: Number(form.endMonth),
        eligibleGrades: form.eligibleGrades.length > 0 ? form.eligibleGrades : null,
        prorateFirstMonth: form.prorateFirstMonth,
        isActive: form.isActive,
      } as const;

      if (editingDefinition) {
        await updateDefinition({
          recurringFeeId: editingDefinition.id,
          ...payload,
        }).unwrap();
        setFeedback({
          tone: 'success',
          message: `Updated recurring fee "${payload.name}".`,
        });
      } else {
        await createDefinition(payload).unwrap();
        setFeedback({
          tone: 'success',
          message: `Created recurring fee "${payload.name}".`,
        });
      }
      setIsModalOpen(false);
    } catch (error) {
      setForm((current) => ({
        ...current,
        error: error && typeof error === 'object' && 'message' in error
          ? String(error.message)
          : 'Unable to save this recurring fee.',
      }));
    }
  };

  const handleToggleActive = async (definition: RecurringFeeDefinitionItem) => {
    setFeedback(null);
    try {
      await updateDefinition({
        recurringFeeId: definition.id,
        isActive: !definition.isActive,
      }).unwrap();
      setFeedback({
        tone: 'success',
        message: `${definition.name} ${definition.isActive ? 'disabled' : 'enabled'} for future charge generation.`,
      });
    } catch (error) {
      setFeedback({
        tone: 'error',
        message: error && typeof error === 'object' && 'message' in error
          ? String(error.message)
          : 'Unable to update this recurring fee.',
      });
    }
  };

  const handleDelete = async (definition: RecurringFeeDefinitionItem) => {
    setFeedback(null);
    try {
      await deleteDefinition({
        recurringFeeId: definition.id,
      }).unwrap();
      setFeedback({
        tone: 'success',
        message: `Deleted recurring fee "${definition.name}".`,
      });
    } catch (error) {
      setFeedback({
        tone: 'error',
        message: error && typeof error === 'object' && 'message' in error
          ? String(error.message)
          : 'Unable to delete this recurring fee.',
      });
    }
  };

  return (
    <section
      data-testid="administrator-tuition-recurring-fee-manager"
      className="rounded-[12px] border border-border bg-white p-6 shadow-sm"
    >
      <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-[20px] font-semibold text-irongrey">Recurring fees</h2>
          <p className="mt-1 text-sm text-text-secondary">
            Manage school-wide monthly fee schedules for aftercare, transportation, lunch, and other recurring services.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreateModal}
          data-testid="administrator-tuition-recurring-fee-add-button"
          className="inline-flex items-center justify-center gap-2 rounded-[8px] bg-[#2697a2] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#1d7e87]"
        >
          <Icon name="Plus" className="h-4 w-4" />
          Add fee
        </button>
      </div>

      {feedback ? (
        <div
          className={`mb-5 rounded-[10px] border px-4 py-3 text-sm ${
            feedback.tone === 'error'
              ? 'border-[rgba(254,44,59,0.2)] bg-[rgba(254,44,59,0.06)] text-strawberry'
              : 'border-[rgba(38,151,162,0.2)] bg-[rgba(38,151,162,0.08)] text-iron-grey'
          }`}
        >
          {feedback.message}
        </div>
      ) : null}

      {isLoading || isFetching ? (
        <div className="grid gap-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="h-24 animate-pulse rounded-[12px] bg-muted" />
          ))}
        </div>
      ) : definitions.length === 0 ? (
        <div className="rounded-[12px] border border-dashed border-border bg-background-light px-5 py-6 text-sm text-text-secondary">
          No recurring fees configured. Add a fee to start billing for aftercare, transportation, lunch, or other recurring services.
        </div>
      ) : (
        <div className="space-y-3">
          {definitions.map((definition) => (
            <div
              key={definition.id}
              data-testid="administrator-tuition-recurring-fee-card"
              className="rounded-[12px] border border-border bg-surface-light px-5 py-4"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-iron-grey">{definition.name}</h3>
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${categoryBadgeClassName(definition.billingCategory)}`}>
                      {CATEGORY_OPTIONS.find((option) => option.value === definition.billingCategory)?.label ?? 'Other'}
                    </span>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      definition.isActive
                        ? 'bg-[rgba(38,151,162,0.08)] text-[#1d7e87]'
                        : 'bg-muted text-text-secondary'
                    }`}>
                      {definition.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-lg font-semibold text-irongrey">{formatCurrency(definition.monthlyAmount)}</p>
                  <p className="text-sm text-text-secondary">{buildScheduleLabel(definition)}</p>
                  {definition.eligibleGrades.length > 0 ? (
                    <p className="text-xs text-text-secondary">
                      Eligible grades: {definition.eligibleGrades.join(', ')}
                    </p>
                  ) : (
                    <p className="text-xs text-text-secondary">Eligible grades: All grades</p>
                  )}
                  {definition.prorateFirstMonth ? (
                    <p className="text-xs text-text-secondary">First month proration enabled.</p>
                  ) : null}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      void handleToggleActive(definition);
                    }}
                    disabled={busy}
                    className={`inline-flex min-w-[110px] items-center justify-center rounded-[999px] px-3 py-2 text-xs font-medium transition-colors ${
                      definition.isActive
                        ? 'bg-[rgba(38,151,162,0.12)] text-[#1d7e87] hover:bg-[rgba(38,151,162,0.18)]'
                        : 'bg-muted text-text-secondary hover:bg-border/70'
                    } disabled:cursor-not-allowed disabled:opacity-60`}
                  >
                    {definition.isActive ? 'Disable fee' : 'Enable fee'}
                  </button>
                  <button
                    type="button"
                    onClick={() => openEditModal(definition)}
                    className="inline-flex items-center justify-center gap-1.5 rounded-[8px] border border-border px-3 py-2 text-xs font-medium text-iron-grey transition-colors hover:bg-muted"
                  >
                    <Icon name="Pencil" className="h-3.5 w-3.5 text-text-secondary" />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void handleDelete(definition);
                    }}
                    disabled={busy}
                    className="inline-flex items-center justify-center gap-1.5 rounded-[8px] border border-[rgba(254,44,59,0.18)] px-3 py-2 text-xs font-medium text-strawberry transition-colors hover:bg-[rgba(254,44,59,0.06)] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Icon name="Trash2" className="h-3.5 w-3.5" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AppDialogFrame
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingDefinition ? 'Edit recurring fee' : 'Add recurring fee'}
        maxWidthClassName="max-w-3xl"
        panelClassName="rounded-[20px]"
        bodyPaddingClassName="px-6 py-5"
        titleClassName="font-title text-2xl font-semibold text-iron-grey"
        footer={(
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="rounded-[8px] border border-border px-4 py-2 text-sm font-medium text-iron-grey transition-colors hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                void handleSave();
              }}
              disabled={!isFormValid(form) || busy}
              className="rounded-[8px] bg-[#2697a2] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#1d7e87] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? 'Saving...' : editingDefinition ? 'Save changes' : 'Add fee'}
            </button>
          </div>
        )}
      >
        <div data-testid="administrator-tuition-recurring-fee-modal" className="space-y-5">
          {form.error ? (
            <SupportErrorMessage>
              {form.error}
            </SupportErrorMessage>
          ) : null}

          <div className="grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-iron-grey">Fee name</span>
              <input
                type="text"
                data-testid="administrator-tuition-recurring-fee-name-input"
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                placeholder="e.g., Aftercare"
                className="w-full rounded-[10px] border border-border bg-white px-3 py-2.5 text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </label>

            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-iron-grey">Monthly amount</span>
              <div className="flex items-center rounded-[10px] border border-border bg-white px-3">
                <span className="text-sm text-text-secondary">$</span>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  data-testid="administrator-tuition-recurring-fee-monthly-amount-input"
                  value={form.monthlyAmount}
                  onChange={(event) => setForm((current) => ({ ...current, monthlyAmount: event.target.value }))}
                  className="w-full bg-transparent px-2 py-2.5 text-sm text-iron-grey focus:outline-none"
                />
              </div>
            </label>

            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-iron-grey">Billing category</span>
              <select
                data-testid="administrator-tuition-recurring-fee-billing-category-select"
                value={form.billingCategory}
                onChange={(event) => setForm((current) => ({
                  ...current,
                  billingCategory: event.target.value as RecurringFeeBillingCategory,
                }))}
                className="w-full rounded-[10px] border border-border bg-white px-3 py-2.5 text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                {CATEGORY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-iron-grey">Billing day</span>
              <input
                type="number"
                min="1"
                max="28"
                data-testid="administrator-tuition-recurring-fee-billing-day-input"
                value={form.billingDay}
                onChange={(event) => setForm((current) => ({ ...current, billingDay: event.target.value }))}
                className="w-full rounded-[10px] border border-border bg-white px-3 py-2.5 text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </label>

            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-iron-grey">Start month</span>
              <select
                data-testid="administrator-tuition-recurring-fee-start-month-select"
                value={form.startMonth}
                onChange={(event) => setForm((current) => ({ ...current, startMonth: event.target.value }))}
                className="w-full rounded-[10px] border border-border bg-white px-3 py-2.5 text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                {MONTH_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-iron-grey">End month</span>
              <select
                data-testid="administrator-tuition-recurring-fee-end-month-select"
                value={form.endMonth}
                onChange={(event) => setForm((current) => ({ ...current, endMonth: event.target.value }))}
                className="w-full rounded-[10px] border border-border bg-white px-3 py-2.5 text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                {MONTH_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="space-y-3 rounded-[12px] border border-border bg-background-light p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-iron-grey">Eligible grades</p>
                <p className="mt-1 text-xs text-text-secondary">Leave blank to allow every grade.</p>
              </div>
              {form.eligibleGrades.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setForm((current) => ({ ...current, eligibleGrades: [] }))}
                  className="text-xs font-medium text-primary transition-colors hover:text-primary-hover"
                >
                  Clear
                </button>
              ) : null}
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {gradeOptions.map((grade) => {
                const selected = form.eligibleGrades.includes(grade);
                return (
                  <button
                    key={grade}
                    type="button"
                    onClick={() => setForm((current) => ({
                      ...current,
                      eligibleGrades: selected
                        ? current.eligibleGrades.filter((value) => value !== grade)
                        : [...current.eligibleGrades, grade].sort((left, right) => left.localeCompare(right)),
                    }))}
                    className={`rounded-[10px] border px-3 py-2 text-left text-sm transition-colors ${
                      selected
                        ? 'border-primary bg-[rgba(38,151,162,0.08)] text-primary'
                        : 'border-border bg-white text-iron-grey hover:bg-muted'
                    }`}
                  >
                    {grade}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setForm((current) => ({ ...current, prorateFirstMonth: !current.prorateFirstMonth }))}
              className={`inline-flex items-center gap-2 rounded-[999px] px-3 py-2 text-sm font-medium transition-colors ${
                form.prorateFirstMonth
                  ? 'bg-[rgba(38,151,162,0.12)] text-primary'
                  : 'bg-muted text-text-secondary'
              }`}
            >
              <span className={`h-2.5 w-2.5 rounded-full ${form.prorateFirstMonth ? 'bg-primary' : 'bg-text-tertiary'}`} />
              Pro-rate first month
            </button>
            <button
              type="button"
              onClick={() => setForm((current) => ({ ...current, isActive: !current.isActive }))}
              className={`inline-flex items-center gap-2 rounded-[999px] px-3 py-2 text-sm font-medium transition-colors ${
                form.isActive
                  ? 'bg-[rgba(38,151,162,0.12)] text-primary'
                  : 'bg-muted text-text-secondary'
              }`}
            >
              <span className={`h-2.5 w-2.5 rounded-full ${form.isActive ? 'bg-primary' : 'bg-text-tertiary'}`} />
              {form.isActive ? 'Active on save' : 'Inactive on save'}
            </button>
          </div>
        </div>
      </AppDialogFrame>
    </section>
  );
}
